/**
 * The organization tree as a flat, ordered list — for pickers.
 *
 * `path` is the hierarchy (`/shiftagent/agentos/st-tenant-439179649`), `depth` the
 * level. Sorting segment-wise keeps every descendant directly under its ancestor
 * even where locale collation would slot `/a/b-2` between `/a/b` and `/a/b/c`.
 * Indentation is `depth − minDepth`, so a subtree that starts three levels down
 * still renders from the left edge, and a child whose parent fell off the
 * 100-row `listTenants` page still renders (indented) rather than vanishing.
 *
 * Filtering keeps the ancestors of every match — dimmed, still selectable — so
 * a hit is never shown without the context that says which subtree it is in.
 */
import type { TenantResponse } from '../../lib/api-client';

export interface OrgRow {
  id: string;
  tenant: TenantResponse;
  /** Indent level relative to the shallowest organization in the list (0 = top). */
  level: number;
  /** False for an ancestor kept only to show where a match sits. */
  isMatch: boolean;
}

/** What an operator recognises: the host-system id, else the name, else the id. */
export function orgLabel(t: Pick<TenantResponse, 'id' | 'name' | 'external_id'>): string {
  return t.external_id ?? t.name ?? t.id;
}

function segments(path: string): string[] {
  return path.split('/').filter(Boolean);
}

/** Segment-wise compare; a strict prefix (the ancestor) sorts before its descendants. */
export function comparePaths(a: string, b: string): number {
  const sa = segments(a);
  const sb = segments(b);
  const n = Math.min(sa.length, sb.length);
  for (let i = 0; i < n; i++) {
    const c = sa[i]!.localeCompare(sb[i]!);
    if (c !== 0) return c;
  }
  return sa.length - sb.length;
}

/** Hierarchy order: every organization directly after its ancestor, siblings by name. */
export function sortByPath(tenants: readonly TenantResponse[]): TenantResponse[] {
  return [...tenants].sort((a, b) => comparePaths(a.path, b.path));
}

/**
 * The rows a picker renders for `query`. Empty query → the whole tree. Otherwise
 * every organization whose external id, name, slug or id contains the query
 * (case-insensitive), plus each match's ancestors that are present in the list.
 */
export function flattenOrgTree(tenants: readonly TenantResponse[], query: string): OrgRow[] {
  const sorted = sortByPath(tenants);
  if (sorted.length === 0) return [];
  const minDepth = Math.min(...sorted.map((t) => t.depth));
  const row = (t: TenantResponse, isMatch: boolean): OrgRow => ({
    id: t.id,
    tenant: t,
    level: Math.max(0, t.depth - minDepth),
    isMatch,
  });

  const q = query.trim().toLowerCase();
  if (q === '') return sorted.map((t) => row(t, true));

  const matches = new Set<string>();
  for (const t of sorted) {
    const fields = [t.external_id, t.name, t.slug, t.id];
    if (fields.some((v) => typeof v === 'string' && v.toLowerCase().includes(q))) matches.add(t.id);
  }

  // Ancestors are resolved by path prefix, never by parent_id: a parent that is
  // missing from the list (truncated page) is simply absent, and its children
  // still render at their own level.
  const byPath = new Map(sorted.map((t) => [t.path, t] as const));
  const keep = new Set(matches);
  for (const t of sorted) {
    if (!matches.has(t.id)) continue;
    const segs = segments(t.path);
    for (let k = segs.length - 1; k >= 1; k--) {
      const ancestor = byPath.get(`/${segs.slice(0, k).join('/')}`);
      if (ancestor) keep.add(ancestor.id);
    }
  }

  return sorted.filter((t) => keep.has(t.id)).map((t) => row(t, matches.has(t.id)));
}
